import { TextStyle } from "react-native"
import { color, nativeBaseTheme, typography } from "../../theme"

/**
 * All text will start off looking like this.
 */
const BASE: TextStyle = {
  fontFamily: typography.primary,
  fontSize: 16,
}

export const presetsColors = (colorScheme = 'light') => ({
  color: colorScheme === 'light' ? color.text : nativeBaseTheme.colors.lightText,
})

/**
 * All the variations of text styling within the app.
 *
 * You want to customize these to whatever you need in your app.
 */
export const presets = (colorScheme = 'light') => ({
  /**
   * The default text styles.
   */
  default: { ...BASE, ...presetsColors(colorScheme) } as TextStyle,

  /**
   * A bold version of the default text.
   */
  bold: { ...BASE, ...presetsColors(colorScheme), fontWeight: "bold" } as TextStyle,

  /**
   * Large headers.
   */
  header: { ...BASE, ...presetsColors(colorScheme), fontSize: 24, fontWeight: "bold" } as TextStyle,

  /**
   * Field labels that appear on forms above the inputs.
   */
  fieldLabel: { ...BASE, ...presetsColors(colorScheme), fontWeight: "700", fontSize: 16, lineHeight: 24, letterSpacing: 0.5 } as TextStyle,

  /**
   * A smaller piece of secondary information.
   */
  secondary: { ...BASE, ...presetsColors(colorScheme), fontSize: 9, color: color.dim } as TextStyle,

  display1: {...BASE, ...presetsColors(colorScheme), fontWeight: "900", fontSize: 28, lineHeight: 36, letterSpacing: 1} as TextStyle,
  display2: {...BASE, ...presetsColors(colorScheme), fontWeight: "900", fontSize: 22, lineHeight: 28, letterSpacing: 1} as TextStyle,
  display3: {...BASE, ...presetsColors(colorScheme), fontWeight: "900", fontSize: 18, lineHeight: 24, letterSpacing: 0.7} as TextStyle,
  display4: {...BASE, ...presetsColors(colorScheme), fontWeight: "900", fontSize: 16, lineHeight: 24, letterSpacing: 0.7} as TextStyle,
  display5: {...BASE, ...presetsColors(colorScheme), fontWeight: "900", fontSize: 14, lineHeight: 24, letterSpacing: 0.7} as TextStyle,
  display6: {...BASE, ...presetsColors(colorScheme), fontWeight: "900", fontSize: 12, lineHeight: 24, letterSpacing: 0.7} as TextStyle,

  displayTitle1: {...BASE, ...presetsColors(colorScheme), fontWeight: "700", fontSize: 47, lineHeight: 20, letterSpacing: 0.5} as TextStyle,
  displayTitle2: {...BASE, ...presetsColors(colorScheme), fontWeight: "700", fontSize: 14, lineHeight: 20, letterSpacing: 0.5} as TextStyle,

  title1: {...BASE, ...presetsColors(colorScheme), fontWeight: "700", fontSize: 20, lineHeight: 28, letterSpacing: 0.5} as TextStyle,
  title2: {...BASE, ...presetsColors(colorScheme), fontWeight: "700", fontSize: 18, lineHeight: 24, letterSpacing: 0.5} as TextStyle,
  title3: {...BASE, ...presetsColors(colorScheme), fontWeight: "700", fontSize: 16, lineHeight: 24, letterSpacing: 0.5} as TextStyle,
  title4: {...BASE, ...presetsColors(colorScheme), fontWeight: "700", fontSize: 14, lineHeight: 20, letterSpacing: 0.5} as TextStyle,
  title5: {...BASE, ...presetsColors(colorScheme), fontWeight: "700", fontSize: 12, lineHeight: 16, letterSpacing: 0.5} as TextStyle,
  title6: {...BASE, ...presetsColors(colorScheme), fontWeight: "700", fontSize: 11, lineHeight: 16, letterSpacing: 0.5} as TextStyle,

  body1: {...BASE, ...presetsColors(colorScheme), fontWeight: "400", fontSize: 16, lineHeight: 20} as TextStyle,
  body2: {...BASE, ...presetsColors(colorScheme), fontWeight: "400", fontSize: 14, lineHeight: 20 } as TextStyle,
  body3: {...BASE, ...presetsColors(colorScheme), fontWeight: "400", fontSize: 12, lineHeight: 16} as TextStyle,
  body4: {...BASE, ...presetsColors(colorScheme), fontWeight: "400", fontSize: 10, lineHeight: 14} as TextStyle,

  label: {...BASE, ...presetsColors(colorScheme), fontWeight: "600", fontSize: 13, lineHeight: 16, letterSpacing: 0.7, textTransform: "uppercase"} as TextStyle,
  label2: {...BASE, ...presetsColors(colorScheme), fontWeight: "600", fontSize: 11, lineHeight: 16} as TextStyle,
  label3: {...BASE, ...presetsColors(colorScheme), fontWeight: "400", fontSize: 9, lineHeight: 16} as TextStyle,

  textBase: {...BASE, ...presetsColors(colorScheme), fontWeight: "400", fontSize: 13, lineHeight: 18} as TextStyle,

  bottomLabel1: {...BASE, ...presetsColors(colorScheme), fontFamily: typography.secondary, fontWeight: "600", fontSize: 12, lineHeight: 14,} as TextStyle,
  bottomLabel2: {...BASE, ...presetsColors(colorScheme), fontFamily: typography.secondary, fontWeight: "400", fontSize: 12, lineHeight: 14} as TextStyle,



  caption1: {...BASE, ...presetsColors(colorScheme), fontWeight: "700", fontSize: 12, lineHeight: 16, letterSpacing: 0.3} as TextStyle,
  caption2: {...BASE, ...presetsColors(colorScheme), fontWeight: "400", fontSize: 12, lineHeight: 16, letterSpacing: 0.3} as TextStyle,

  headingBase: {...BASE, ...presetsColors(colorScheme), fontFamily: typography.secondary, fontWeight: "700", fontSize: 14, lineHeight: 24} as TextStyle,

  xs: {...BASE, ...presetsColors(colorScheme), fontFamily: typography.secondary, fontWeight: "400", fontSize: 11, lineHeight: 16} as TextStyle
})

export const presetsLight = presets()

/**
 * A list of preset names.
 */
export type TextPresets = keyof typeof presetsLight
